/**
 * prohats-gallery.js
 * ─────────────────────────────────────────────────────────────────────────
 * Web Component that encapsulates the full interactive logic of the React
 * product gallery (components/product/gallery.tsx).
 * ─────────────────────────────────────────────────────────────────────────
 */

class ProHatsProductGallery extends HTMLElement {
  constructor() {
    super();
    this.activeImageIndex = 0;
    this.isLightboxOpen = false;
    this.lightboxIndex = 0;
    this.isZoomed = false;

    // Read attributes
    this.imagesCount = parseInt(this.getAttribute('data-images-count') || '0', 10);
  }

  connectedCallback() {
    this.initElements();
    this.bindEvents();
    this.initMobileSlider();
    this.initScrollZoom();
  }

  initElements() {
    // Select elements inside this component
    this.mobileSlider = this.querySelector('.ph-gallery-slider');
    this.dots = this.querySelectorAll('.ph-gallery-dot');
    this.zoomImages = this.querySelectorAll('.ph-zoom-image');
    this.mobileSlides = this.querySelectorAll('.ph-gallery-slide');
    
    // Lightbox nodes
    this.lightbox = this.querySelector('.ph-lightbox');
    this.lightboxImgWrap = this.querySelector('.ph-lightbox-image-wrap');
    this.lightboxImg = this.querySelector('.ph-lightbox-img');
    this.lightboxCloseBtn = this.querySelector('.ph-lightbox-close');
    this.lightboxPrevBtn = this.querySelector('.ph-lightbox-prev');
    this.lightboxNextBtn = this.querySelector('.ph-lightbox-next');
    this.lightboxCounter = this.querySelector('.ph-lightbox-counter');
    
    // Gather image src list from data attributes of main/stacked/slide items
    this.imageSources = [];
    
    // Desktop sticky list layout active class
    const desktopGallery = this.querySelector('.ph-gallery-desktop');
    if (this.imagesCount > 1 && desktopGallery) {
      desktopGallery.classList.add('ph-gallery-stacked-layout-active');
    }

    // Capture sources in order of data-index
    const allItems = Array.from(this.querySelectorAll('[data-src]'));
    const srcMap = {};
    allItems.forEach(item => {
      const idx = parseInt(item.getAttribute('data-index') || '0', 10);
      const src = item.getAttribute('data-src');
      const alt = item.querySelector('img') ? item.querySelector('img').getAttribute('alt') : '';
      srcMap[idx] = { src, alt };
    });
    
    for (let i = 0; i < this.imagesCount; i++) {
      if (srcMap[i]) {
        this.imageSources.push(srcMap[i]);
      }
    }
  }

  bindEvents() {
    // Click on desktop images opens lightbox
    this.zoomImages.forEach(zoomImg => {
      zoomImg.addEventListener('click', () => {
        const index = parseInt(zoomImg.getAttribute('data-index') || '0', 10);
        this.openLightbox(index);
      });
      
      // Cursor follower tracking
      const follower = zoomImg.querySelector('.ph-cursor-follower');
      if (follower) {
        zoomImg.addEventListener('mousemove', (e) => {
          const rect = zoomImg.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          follower.style.left = `${x}px`;
          follower.style.top = `${y}px`;
        });
      }
    });

    // Click on mobile slides opens lightbox
    this.mobileSlides.forEach(slide => {
      slide.addEventListener('click', () => {
        const index = parseInt(slide.getAttribute('data-index') || '0', 10);
        this.openLightbox(index);
      });
    });

    // Mobile slider dots click
    this.dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.getAttribute('data-dot-index') || '0', 10);
        this.scrollToImage(index);
      });
    });

    // Lightbox events
    if (this.lightboxCloseBtn) {
      this.lightboxCloseBtn.addEventListener('click', () => this.closeLightbox());
    }
    if (this.lightboxPrevBtn) {
      this.lightboxPrevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.prevSlide();
      });
    }
    if (this.lightboxNextBtn) {
      this.lightboxNextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.nextSlide();
      });
    }
    if (this.lightboxImgWrap) {
      this.lightboxImgWrap.addEventListener('click', (e) => {
        this.toggleLightboxZoom(e);
      });
      this.lightboxImgWrap.addEventListener('mousemove', (e) => {
        this.handleLightboxPan(e);
      });
    }

    // Keyboard accessibility for Lightbox
    this._handleKeyDown = (e) => {
      if (!this.isLightboxOpen) return;
      if (e.key === 'Escape') {
        this.closeLightbox();
      } else if (e.key === 'ArrowRight' && this.imagesCount > 1) {
        this.nextSlide();
      } else if (e.key === 'ArrowLeft' && this.imagesCount > 1) {
        this.prevSlide();
      }
    };
    window.addEventListener('keydown', this._handleKeyDown);
  }

  disconnectedCallback() {
    if (this._handleKeyDown) {
      window.removeEventListener('keydown', this._handleKeyDown);
    }
    if (this._handleScroll) {
      window.removeEventListener('scroll', this._handleScroll);
    }
  }

  // ── Mobile Slider Logic ──
  initMobileSlider() {
    if (!this.mobileSlider) return;
    this.mobileSlider.addEventListener('scroll', () => {
      const scrollLeft = this.mobileSlider.scrollLeft;
      const width = this.mobileSlider.clientWidth;
      if (width > 0) {
        const index = Math.round(scrollLeft / width);
        if (index !== this.activeImageIndex && index >= 0 && index < this.imagesCount) {
          this.activeImageIndex = index;
          this.updateActiveDot(index);
        }
      }
    }, { passive: true });
  }

  scrollToImage(index) {
    if (this.mobileSlider) {
      const width = this.mobileSlider.clientWidth;
      this.mobileSlider.scrollTo({
        left: width * index,
        behavior: 'smooth'
      });
      this.activeImageIndex = index;
      this.updateActiveDot(index);
    }
  }

  updateActiveDot(index) {
    this.dots.forEach(dot => {
      const dotIndex = parseInt(dot.getAttribute('data-dot-index') || '0', 10);
      if (dotIndex === index) {
        dot.classList.add('ph-gallery-dot--active');
      } else {
        dot.classList.remove('ph-gallery-dot--active');
      }
    });
  }

  // ── Scroll Zoom Logic ──
  initScrollZoom() {
    const mainStickyImg = this.querySelector('.ph-gallery-main-sticky .ph-gallery-img');
    if (!mainStickyImg) return;

    this._handleScroll = () => {
      const scrollTop = window.scrollY;
      const maxScroll = 600;
      const pct = Math.min(scrollTop / maxScroll, 1);
      const scale = 1 + pct * 0.20; // 1.0 to 1.20
      mainStickyImg.style.transform = `scale(${scale})`;
      mainStickyImg.style.transition = 'transform 0.1s ease-out';
    };

    window.addEventListener('scroll', this._handleScroll, { passive: true });
  }

  // ── Lightbox Logic ──
  openLightbox(index) {
    this.lightboxIndex = index;
    this.isLightboxOpen = true;
    this.isZoomed = false;
    
    if (this.lightbox) {
      this.lightbox.setAttribute('aria-hidden', 'false');
    }
    
    document.body.style.overflow = 'hidden'; // Lock page scroll
    this.updateLightboxImage();
  }

  closeLightbox() {
    this.isLightboxOpen = false;
    if (this.lightbox) {
      this.lightbox.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = ''; // Unlock page scroll
    this.resetLightboxZoom();
  }

  prevSlide() {
    this.lightboxIndex = (this.lightboxIndex - 1 >= 0) ? this.lightboxIndex - 1 : this.imagesCount - 1;
    this.resetLightboxZoom();
    this.updateLightboxImage();
  }

  nextSlide() {
    this.lightboxIndex = (this.lightboxIndex + 1 < this.imagesCount) ? this.lightboxIndex + 1 : 0;
    this.resetLightboxZoom();
    this.updateLightboxImage();
  }

  updateLightboxImage() {
    const activeImgObj = this.imageSources[this.lightboxIndex];
    if (activeImgObj && this.lightboxImg) {
      this.lightboxImg.src = activeImgObj.src;
      this.lightboxImg.alt = activeImgObj.alt || 'Product image fullscreen';
    }
    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${this.lightboxIndex + 1} / ${this.imagesCount}`;
    }
  }

  toggleLightboxZoom(e) {
    this.isZoomed = !this.isZoomed;
    if (this.isZoomed) {
      this.lightboxImgWrap.classList.add('ph-lightbox-zoomed');
      const rect = this.lightboxImgWrap.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      this.lightboxImg.style.transformOrigin = `${x}% ${y}%`;
      this.lightboxImg.style.transform = 'scale(2.2)';
    } else {
      this.resetLightboxZoom();
    }
  }

  handleLightboxPan(e) {
    if (!this.isZoomed) return;
    const rect = this.lightboxImgWrap.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    this.lightboxImg.style.transformOrigin = `${x}% ${y}%`;
  }

  resetLightboxZoom() {
    this.isZoomed = false;
    if (this.lightboxImgWrap) {
      this.lightboxImgWrap.classList.remove('ph-lightbox-zoomed');
    }
    if (this.lightboxImg) {
      this.lightboxImg.style.transformOrigin = '';
      this.lightboxImg.style.transform = '';
    }
  }
}

customElements.define('prohats-product-gallery', ProHatsProductGallery);
